// index.tsx
// SurgeonList component for displaying, searching, and managing a list of subscribed surgeons.
// Includes UI for filtering, unsubscribing, and viewing surgeon details, with feedback and error handling.

import { useState, ChangeEvent, FormEvent } from "react";
import {
  Input,
  Card,
  Avatar,
  Heading,
  Button,
  Modal,
  Toast,
  StatusTag,
} from "@Forge/dist";
import Loader from "@/components/Loader";
import { styled } from "@stitches/react";
import { SurgeonSubscribed } from "@/types/surgeons";
import { getInitials } from "@/utils/helpers";
import { AlertIcon } from "../Icons";
import { useSurgeons } from "@/hooks/use-surgeons";

/**
 * Container for the full list of subscribed surgeons.
 */
const SubscribedSurgeonsContainer = styled("div", {
  flex: 1,
});

/**
 * List wrapper for surgeon cards.
 */
const SurgeonCardList = styled("div", {
  display: "flex",
  padding: "16px 5px",
  gap: "24px",
  flexWrap: "wrap",
  justifyContent: "flex-start",
  alignContent: "flex-start",
  height: "calc(100vh - 250px)",
  overflowY: "scroll",
  marginTop: "8px",
});

/**
 * Row layout utility.
 */
const Row = styled("div", {
  display: "flex",
  gap: "12px",
  alignItems: "center",
});

/**
 * SurgeonCardDetail Component
 * Displays a label and value for a surgeon's detail (e.g., specialty, NPI).
 * @param label - The label for the detail
 * @param value - The value for the detail
 */
const SurgeonCardDetail = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => {
  return (
    <Row css={{ justifyContent: "flex-start" }}>
      <Heading
        style={{ margin: 0, fontSize: "16px" }}
        variant="subsection"
        text={`${label}:`}
      />
      {value}
    </Row>
  );
};

/**
 * SurgeonCard Component
 * Displays a card with surgeon info, avatar, and unsubscribe button/modal.
 * Handles unsubscribe logic and feedback.
 * @param surgeon - The surgeon data
 * @param setToastData - Callback to set toast feedback
 */
const SurgeonCard = ({
  surgeon: { surgeon, specialty, npi, providerActive },
  setToastData,
}: {
  surgeon: SurgeonSubscribed;
  setToastData: (data: {
    message: string;
    header: string;
    type: "success" | "attention" | "info";
  }) => void;
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const {
    delete: {
      unsubscribe,
      error: unsubscribeError,
      isError: isUnsubscribingError,
      isPending: isUnsubscribing,
    },
  } = useSurgeons();
  return (
    <Card style={{ width: "350px", height: "140px" }}>
      <Row css={{ justifyContent: "space-between", marginBottom: "8px" }}>
        <Row>
          <Avatar
            style={{
              borderRadius: "4px",
              filter: providerActive ? "" : "grayscale(100%)",
            }}
            size="medium"
            initials={getInitials(surgeon)}
          />
          <div>
            <Heading
              style={{
                marginBottom: "0",
                color: providerActive ? "" : "#B6B7B7",
              }}
              variant="subsection"
              text={surgeon}
            />
            {!providerActive && (
              <StatusTag
                style={{ color: "#373738", backgroundColor: "#DBDBDB" }}
                text="Provider Not Available"
              />
            )}
          </div>
        </Row>
        <Button
          icon="Delete"
          variant="tertiary"
          onClick={() => setIsModalOpen(true)}
        />
      </Row>
      <SurgeonCardDetail
        label="Specialty"
        value={specialty ?? "Not Available"}
      />
      <SurgeonCardDetail label="NPI" value={npi ?? "Not Available"} />
      <Modal
        width="medium"
        show={isModalOpen}
        onHide={() => setIsModalOpen(false)}
        headerText={`Are you sure you'd like to remove ${surgeon}?`}
        primaryButtonText="Remove"
        onPrimaryClick={() => {
          unsubscribe(npi, () => {
            return new Promise(() => {
              setToastData({
                message: `${surgeon} has been removed from your view.`,
                header: `You've removed ${surgeon}`,
                type: "success",
              });
              setIsModalOpen(false);
            });
          });
        }}
        includeButtonRow={!isUnsubscribing}
      >
        <Loader
          displayText={`Removing ${surgeon} ...`}
          isLoading={isUnsubscribing}
          isError={isUnsubscribingError}
          errorReason={unsubscribeError?.message}
        >
          <Row>
            <AlertIcon /> This will remove {surgeon} from your view.
          </Row>
        </Loader>
      </Modal>
    </Card>
  );
};

/**
 * SurgeonList Component
 * Displays a searchable list of subscribed surgeons, with the ability to unsubscribe.
 * Handles loading, error, and feedback states.
 */
const SurgeonList = () => {
  const {
    get: {
      data: subscribedSurgeons,
      isPending: isGettingSurgeons,
      isError: isGetError,
      error: getError,
    },
  } = useSurgeons();

  const [toastData, setToastData] = useState<{
    message: string;
    header: string;
    type: "success" | "attention" | "info";
  } | null>(null);

  const [filterTerm, setFilterTerm] = useState<string>("");

  /**
   * Generates the list of surgeon cards, filtered by search term if present.
   */
  const generateCards = () => {
    if (!subscribedSurgeons?.length)
      return (
        <Heading
          variant="subsection"
          text="No surgeons have been assigned. Please search and add a surgeon."
        />
      );
    if (filterTerm) {
      return subscribedSurgeons
        ?.filter((surgeon) =>
          surgeon.surgeon.toLowerCase().includes(filterTerm.toLowerCase()),
        )
        .map((surgeon) => (
          <SurgeonCard
            setToastData={setToastData}
            surgeon={surgeon}
            key={surgeon.npi}
          />
        ));
    }
    return subscribedSurgeons?.map((surgeon) => (
      <SurgeonCard
        setToastData={setToastData}
        surgeon={surgeon}
        key={surgeon.npi}
      />
    ));
  };

  return (
    <SubscribedSurgeonsContainer>
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
        }}
        style={{ width: "350px" }}
      >
        <Input
          placeholder="Search your surgeons"
          type="text"
          name="search"
          icon="Search"
          clearable
          minLength={4}
          value={filterTerm}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setFilterTerm(e.target.value)
          }
          data-testid="test-filter-surgeons"
        />
      </form>
      <Loader
        displayText="Getting Subscribed Surgeons..."
        isLoading={isGettingSurgeons}
        isError={isGetError}
        errorReason={getError?.message}
      >
        <SurgeonCardList>{generateCards()}</SurgeonCardList>
      </Loader>
      {toastData && (
        <Toast
          id="surgeon-toast"
          headerText={toastData?.header}
          alertType={toastData?.type}
          onDismiss={() => setToastData(null)}
        >
          <p>{toastData?.message}</p>
        </Toast>
      )}
    </SubscribedSurgeonsContainer>
  );
};

export default SurgeonList;
